import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// --- IMPORTING ALL COMPONENTS & PAGES ---
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import FreeTrial from './pages/FreeTrial';
import PublicSubscribeGateway from './pages/PublicSubscribeGateway';
import RegisterAfterPayment from './pages/RegisterAfterPayment';
import GoogleAuthSuccess from './pages/GoogleAuthSuccess';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import Subscription from './pages/Subscription';
import Setup from './pages/Setup';
import Dashboard from './pages/Dashboard';
import PatientHistory from './pages/PatientHistory';
import PricingEngine from './pages/PricingEngine';
import InventorySetup from './pages/InventorySetup';
import AuditDetail from './pages/AuditDetail';
import BusinessReports from './pages/BusinessReports';
import AutoReminders from './pages/AutoReminders';
import SSOLogin from './pages/SSOLogin';
import LabAndConsultantLedger from './pages/LabAndConsultantLedger';
import PublicLabUpdate from './pages/PublicLabUpdate';
import PublicBooking from './pages/PublicBooking';
import ManageAppointment from './pages/ManageAppointment';
import ManageFollowUp from './pages/ManageFollowUp';
import PublicPatientDocument from './pages/PublicPatientDocument';
import StaffAttendance from './pages/StaffAttendance';
import TeamAttendance from './pages/TeamAttendance';
import MonthlyReports from './pages/MonthlyReports';
import DoctorsCircle from './pages/DoctorsCircle';
import SupplyHub from './pages/SupplyHub';
import ExternalConsultants from './pages/ExternalConsultants';
import SaasBusinessControl from './pages/SaasBusinessControl';
import FollowUps from './pages/FollowUps';
import AddOns from './pages/AddOns';
import PrescriptionPad from './components/PrescriptionPad';
import ClinicWorkflow from './pages/ClinicWorkflow';

// --- IMPORTING THE NEW PAGES WE JUST BUILT ---
import PrintDocuments from './pages/PrintDocuments';
import MakeInvoice from './pages/MakeInvoice';

function SignupRedirect() {
  const location = useLocation();
  return <Navigate to={`/subscribe${location.search || ''}`} replace />;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/free-trial" element={<FreeTrial />} />
        <Route path="/signup" element={<SignupRedirect />} />
        <Route path="/subscribe" element={<PublicSubscribeGateway />} />
        <Route path="/register-after-payment" element={<RegisterAfterPayment />} />
        <Route path="/google-auth/success" element={<GoogleAuthSuccess />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-failed" element={<PaymentFailed />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/sso-login" element={<SSOLogin />} />
        <Route path="/lab-update/:token" element={<PublicLabUpdate />} />
        <Route path="/book/:identifier" element={<PublicBooking />} />
        <Route path="/manage-appointment/:token" element={<ManageAppointment />} />
        <Route path="/follow-up/:token" element={<ManageFollowUp />} />
        <Route path="/patient-document/:token" element={<PublicPatientDocument />} />

        {/* Protected Routes (Only accessible after login) */}
        <Route element={<ProtectedRoute />}>
          {/* Default redirect to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Core Sidebar Pages */}
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/subscription/renew" element={<Subscription />} />
          <Route path="/subscription-expired" element={<Subscription />} />
          <Route path="/setup" element={<Layout><Setup /></Layout>} />
          <Route path="/clinic-workflow" element={<Layout><ClinicWorkflow /></Layout>} />
          <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
            <Route path="/patients" element={<Layout><PatientHistory /></Layout>} />
            <Route path="/follow-ups" element={<Layout><FollowUps /></Layout>} />
            <Route path="/pricing" element={<Layout><PricingEngine /></Layout>} />
            <Route path="/inventory" element={<Layout><InventorySetup /></Layout>} />
            <Route path="/inventory/audit/:invoiceId" element={<Layout><AuditDetail /></Layout>} />
            <Route path="/reports" element={<Layout><BusinessReports /></Layout>} />
            <Route path="/consultant-ledger" element={<Layout><LabAndConsultantLedger /></Layout>} />
            <Route path="/lab-consultant-ledger" element={<Layout><LabAndConsultantLedger /></Layout>} />
            <Route path="/staff-attendance" element={<Layout><TeamAttendance /></Layout>} />
            <Route path="/team-attendance" element={<Layout><TeamAttendance /></Layout>} />
            <Route path="/staff-attendance-legacy" element={<Layout><StaffAttendance /></Layout>} />
            <Route path="/external-consultants" element={<Layout><ExternalConsultants /></Layout>} />
            <Route path="/monthly-reports" element={<Layout><MonthlyReports /></Layout>} />
            <Route path="/doctors-circle" element={<Layout><DoctorsCircle /></Layout>} />
            <Route path="/supply-hub" element={<Layout><SupplyHub /></Layout>} />
            <Route path="/addons" element={<Layout><AddOns /></Layout>} />
            <Route path="/platform-admin/business-control" element={<Layout><SaasBusinessControl /></Layout>} />

          {/* NEW ACTION PAGES (Wired to Dashboard Buttons) */}
          <Route path="/emi-reminders" element={<Layout><AutoReminders /></Layout>} />
          <Route path="/print-documents" element={<Layout><PrintDocuments /></Layout>} />
          <Route path="/prescriptions/new" element={<Layout><PrescriptionPad /></Layout>} />
          <Route path="/invoices/new" element={<Layout><MakeInvoice /></Layout>} />
        </Route>

        {/* Catch-all: If someone types a random URL, send them to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
